// React
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Amplify
import { signIn } from "aws-amplify/auth";

// Material UI
import { Container, Button, Box, InputAdornment } from "@mui/material";
import { MailOutline, LockOutlined } from "@mui/icons-material";

// Formik
import { useFormik } from "formik";

// Components
import ConfirmSignUp from "../ConfirmSignUp";
import { FormikTextField } from "Common/Components";

// Redux
import { useDispatch } from "react-redux";
import { setAuthState, setUserEmail } from "../../../Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";
import { refreshApiSession } from "Auth/apiSession";
import { setActiveAuthDomain } from "Auth/AuthDomain";
import { clearAppSessionStorage } from "Auth/authUtils";

// Styles
import { styles } from "./styles";
import { boxStyles } from "Common/Styles/BoxLayouts";

// Validation
import { signinValidationSchema } from "Validation/UserValidation";

// Constants
import { AUTH_DOMAINS } from "Constants/AuthConstants";

const SignIn = () => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/");
  }, [navigate]);

  const handleSignIn = async (email, password) => {
    try {
      clearAppSessionStorage();
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      });
      dispatch(setUserEmail(email));
      if (!isSignedIn) {
        switch (nextStep.signInStep) {
          case "RESET_PASSWORD":
            dispatch(
              openSnackbar({
                message: t("signIn.adminPasswordResetRequested"),
                severity: "warning",
              })
            );
            dispatch(setAuthState("ResetPassword"));
            break;
          case "CONFIRM_SIGN_UP":
            setConfirmOpen(true);
            break;
          default:
            setConfirmOpen(true);
            break;
        }
      } else {
        setActiveAuthDomain(AUTH_DOMAINS.PRIMARY);
        await refreshApiSession();
        dispatch(setAuthState("SignedIn"));
      }
    } catch (error) {
      const errorMessages = {
        UserNotConfirmedException: t("signIn.userNotConfirmed"),
        NotAuthorizedException: t("signIn.invalidCredentials"),
        UserNotFoundException: t("errors.userNotFound"),
      };

      const errorMessage = errorMessages[error.name] || error.message;

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    }
  };

  const handleForgotPassword = () => {
    dispatch(setUserEmail(null));
    dispatch(setAuthState("ResetPassword"));
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: signinValidationSchema(t),
    onSubmit: (values) => {
      handleSignIn(values.email, values.password);
    },
  });

  return (
    <Container>
      <Box component="form" onSubmit={formik.handleSubmit} sx={styles.formBox}>
        <Box sx={styles.textFieldsBox}>
          <FormikTextField
            formik={formik}
            name="email"
            field={{
              type: "email",
              label: t("common.email"),
              required: false,
              autoComplete: "email",
              slotProps: {
                htmlInput: { "aria-required": true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
          <FormikTextField
            formik={formik}
            name="password"
            field={{
              type: "password",
              label: t("common.password"),
              required: false,
              autoComplete: "current-password",
              slotProps: {
                htmlInput: { "aria-required": true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </Box>
        <Box sx={boxStyles.verticalButtonGroupBox}>
          <Button type="submit" variant="contained" color="secondary" fullWidth>
            {t("signIn.signInButton")}
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={handleForgotPassword}
            sx={styles.forgotPasswordBtn}
          >
            {t("signIn.forgotPassword")}
          </Button>
        </Box>
      </Box>

      <ConfirmSignUp
        open={confirmOpen}
        onClose={handleConfirmClose}
        password={formik.values.password}
      />
    </Container>
  );
};

export default SignIn;
